import React, { useMemo } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Button, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@emotion/react';
import { Link } from 'react-router-dom';

import useClasses from '../../../hooks/legacy/useClasses';

import styles from './HomeSection.style';
import { getConfig } from '../../../AppConfig';
import useDesktopCheck from '../../../util/hooks/UseDesktopCheck';
import { isConsumerApp, isRestaurantApp } from '../../../util/Helpers';

const config = getConfig();
const isConsumerWeb = isConsumerApp();
const isRestaurantWeb = isRestaurantApp();

function HomeSection() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const isDesktop = useDesktopCheck();

  const sectionData = useMemo(() => {
    const maxItems = 6;
    const resultArray = [];
    if (isConsumerWeb) {
      for (let id = 1; id <= maxItems; id++) {
        const hasFeature = config.locales.find(
          (item) => item?.name === `has_feature_0${id}`
        );

        if (hasFeature) {
          const title = config.locales.find(
            (item) => item?.name === `feature_title_0${id}`
          )?.text;
          const description = config.locales.find(
            (item) => item?.name === `feature_text_0${id}`
          )?.text;
          const button = config.locales.find(
            (item) => item?.name === `feature_button_0${id}`
          )?.text;
          const buttonLink = config.locales.find(
            (item) => item?.name === `feature_link_0${id}`
          )?.text;

          const image = config.images.find(
            (img) => img?.name === `feature_image_0${id}`
          )?.url;

          resultArray.push({
            id,
            hasFeature: hasFeature?.text,
            image,
            title,
            description,
            button,
            buttonLink,
          });
        }
      }
    }
    if (isRestaurantWeb) {
      for (let id = 1; id <= maxItems; id++) {
        const hasFeature = config.locales.find(
          (item) => item?.name === `has_custom_0${id}`
        );

        if (hasFeature) {
          const title = config.locales.find(
            (item) => item?.name === `custom_title_0${id}`
          )?.text;
          const description = config.locales.find(
            (item) => item?.name === `custom_text_0${id}`
          )?.text;
          const button = config.locales.find(
            (item) => item?.name === `custom_button_0${id}`
          )?.text;
          const buttonLink = config.locales.find(
            (item) => item?.name === `custom_link_0${id}`
          )?.text;

          const image = config.images.find(
            (img) => img?.name === `custom_image_0${id}`
          )?.url;

          resultArray.push({
            id,
            hasFeature: hasFeature?.text,
            image,
            title,
            description,
            button,
            buttonLink,
          });
        }
      }
    }
    return resultArray;
  }, [config]);

  const renderRightFeature = (feature) => (
    <Grid container spacing={4} key={feature.id} mb={2}>
      <Grid item md={12} lg={6}>
        {feature?.image ? (
          <Box
            component="img"
            sx={{ height: '365px', width: '100%' }}
            src={
              feature.image.includes('https')
                ? feature.image
                : `https:${feature.image}`
            }
            className={classes.sectionImg}
          />
        ) : (
          <Skeleton
            height={365}
            width="100%"
            variant="rectangular"
            sx={{ borderRadius: 3 }}
          />
        )}
      </Grid>
      <Grid item md={12} lg={6}>
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
              mt: 4,
            }}
          >
            <Typography variant="h5" color="black">
              {feature?.title ? feature?.title : ''}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body1" color="black">
              {feature?.description ? feature?.description : ''}
            </Typography>
          </Box>
          {feature?.button ? (
            <Box sx={{ mt: 4, mb: 4 }}>
              <Link to={feature?.buttonLink}>
                <Button
                  color="primary"
                  variant="contained"
                  data-test-id="learn-more"
                  className={classes.button}
                >
                  {feature.button}
                </Button>
              </Link>
            </Box>
          ) : null}
        </Box>
      </Grid>
    </Grid>
  );

  const renderLeftFeature = (feature) => (
    <Grid container spacing={4} key={feature.id} mb={2}>
      <Grid item md={12} lg={6}>
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
              mt: 4,
            }}
          >
            <Typography variant="h5" color="black">
              {feature?.title ? feature?.title : ''}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body1" color="black">
              {feature?.description ? feature?.description : ''}
            </Typography>
          </Box>
          {feature?.button ? (
            <Box sx={{ mt: 4, mb: 4 }}>
              <Link to={feature?.buttonLink}>
                <Button
                  color="primary"
                  variant="contained"
                  data-test-id="learn-more"
                  className={classes.button}
                >
                  {feature.button}
                </Button>
              </Link>
            </Box>
          ) : null}
        </Box>
      </Grid>
      <Grid item md={12} lg={6}>
        {feature?.image ? (
          <Box
            component="img"
            sx={{ height: '365px', width: '100%' }}
            src={
              feature.image.includes('https')
                ? feature.image
                : `https:${feature.image}`
            }
            className={classes.sectionImg}
          />
        ) : (
          <Skeleton
            height={365}
            width="100%"
            variant="rectangular"
            sx={{ borderRadius: 3 }}
          />
        )}
      </Grid>
    </Grid>
  );
  if (!sectionData?.length) {
    if (isConsumerWeb) {
      return (
        <Box
          sx={{
            mb: 19,
            [theme.breakpoints.down('sm')]: {
              mb: 5,
            },
          }}
        />
      );
    }
    return null;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 6,
        mb: 10,
        [theme.breakpoints.down('sm')]: {
          mb: 5,
          p: 4,
        },
      }}
    >
      {isDesktop
        ? sectionData.map((feature, index) => (
            <Box key={feature.id} sx={{ mt: 8 }}>
              {index % 2 === 0 &&
                feature.hasFeature === 'true' &&
                renderRightFeature(feature)}
              {index % 2 === 1 &&
                feature.hasFeature === 'true' &&
                renderLeftFeature(feature)}
            </Box>
          ))
        : sectionData.map(
            (feature) =>
              feature.hasFeature === 'true' && renderRightFeature(feature)
          )}
    </Box>
  );
}

export default HomeSection;
